/* app/seo-audit.jsx - Site Audit, at the two positions of the switcher.

   AT ONE SECTION, unchanged. Two crawl sources over one connection:
     • REST  → GET /api/wp/audit         (WordPress + platform seo_meta)
     • Live  → GET /api/wp/audit-public  (fetches served HTML: robots.txt →
               sitemap → each URL's <head>; checks canonical/OG/JSON-LD).
   Live mode is built for headless sites whose served HTML is the source of
   truth, and a crawl-only section keeps the chooser hidden with the live pass
   forced. Both responses share the same {summary, issues} shape.

   AT ALL, the whole website: GET /api/wp/audit-property, the public lens over
   every section (docs/MODULE-SCOPE.md 3.1). That is a DIFFERENT view and not a
   wider version of this one, and 4.1 says why: the public lens reads what the
   website SERVES, which is the property's universe, and the REST lens reads
   what one CMS HOLDS, drafts included, which belongs to one section by
   definition. So the chooser has nothing to offer at All.

   NOTHING IS RUN OR STORED AT ALL EITHER. The stored snapshot is one row per
   site and source, so a property-wide crawl would overwrite a section's own.
   Running and exporting stay PER SECTION, one line each, through the very
   endpoints the section position uses, which is also the per row verb
   MODULE-SCOPE 1.2 asks for: one audit is one job for one owner, and one file
   telling a developer to fix titles across two CMSs and a static build is
   three jobs in one document.
   Indexing (GSC URL Inspection) defers to the GSC pass. */
import { api } from '../lib/api.js';
(function () {
  const { useState, useEffect, useCallback } = React;
  function I(props) { return React.createElement(window.MlIcon, props); }
  const SeoStat = (p) => React.createElement(window.MlSeoDashboard.SeoStat, p);

  // The address a crawl is pointed at, derived exactly as the section position
  // derives it, so a section run from the property screen crawls the same thing
  // it would crawl from its own.
  const fullUrlOf = (s) => {
    const raw = (s && (s.urlFull || s.url)) || "";
    return raw.startsWith("http") ? raw : (raw ? "https://" + raw : "");
  };
  const dayOf = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    return isNaN(d) ? null : d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
  };

  /* ── One section: the screen as it was, two lenses over one connection ──── */
  function SectionAudit({ site }) {
    const fullUrl = fullUrlOf(site);
    const [phase, setPhase] = useState("pre"); // pre | running | results | error
    const [mode, setMode] = useState("rest");  // rest | public
    // A crawl-only site has no CMS, so the WordPress REST pass has nothing to
    // read and the route answers CRAWL_ONLY_SITE. The live pass does not care:
    // it fetches served HTML, which is the only thing such a site has. So the
    // mode is FORCED rather than defaulted, and the chooser is hidden. A
    // default would be undone by one click and then fail with an error that
    // looks like a broken audit instead of a site with no CMS.
    const crawlOnly = !!(site && site.source === "crawl");
    const useMode = crawlOnly ? "public" : mode;
    const [wpFrontEnd, setWpFrontEnd] = useState(""); // optional duplicate-probe base
    const [expanded, setExpanded] = useState({});
    const [res, setRes] = useState(null);
    // When the snapshot on screen was crawled, or null while it is this
    // session's own run. Shown next to the figures, because a stored audit
    // and one that just finished are different things to act on.
    const [storedAt, setStoredAt] = useState(null);

    /* THE LAST AUDIT COMES BACK.
       Every run was already stored, and the screen read none of it: leaving
       and returning showed "Run a site audit" over a site with 99 findings
       sitting in the table, so the only way to see them again was to pay for
       another crawl. The snapshot is loaded for the lens being shown, and a
       site or lens with nothing stored falls back to the empty state exactly
       as before. */
    useEffect(() => {
      if (!fullUrl) return;
      let alive = true;
      setRes(null); setStoredAt(null); setPhase("pre");
      api(`/wp/audit-latest?siteUrl=${encodeURIComponent(fullUrl)}&source=${useMode === "public" ? "public" : "rest"}`)
        .then(r => {
          const a = r && r.audit;
          if (!alive || !a || !Array.isArray(a.issues)) return;
          setRes({ source: a.source, crawled: a.crawled, summary: { error: a.errors, warning: a.warnings, notice: a.notices }, issues: a.issues, meta: a.meta });
          setStoredAt(a.updated_at || null);
          setPhase("results");
        })
        .catch(() => { /* nothing stored, or unreadable: the empty state is right */ });
      return () => { alive = false; };
    }, [fullUrl, useMode]);

    const run = async () => {
      if (!fullUrl) { setPhase("error"); return; }
      setPhase("running");
      try {
        let r;
        if (useMode === "public") {
          const wp = wpFrontEnd.trim();
          r = await api(`/wp/audit-public?siteUrl=${encodeURIComponent(fullUrl)}&maxUrls=100${wp ? "&wpFrontEnd=" + encodeURIComponent(wp) : ""}`);
        } else {
          r = await api(`/wp/audit?siteUrl=${encodeURIComponent(fullUrl)}&perPage=100`);
        }
        setRes(r); setStoredAt(null); setPhase("results");
      } catch { setPhase("error"); }
    };

    const sum = (res && res.summary) || { error: 0, warning: 0, notice: 0 };
    const ISSUES = ((res && res.issues) || []).map(it => ({
      id: it.key, sev: it.severity, label: it.label, count: it.count,
      items: (it.items || []).slice(0, 4).map(x => (x.title || x.link || "") + (x.title && x.link ? " · " + x.link : "")),
      more: Math.max(0, (it.count || 0) - Math.min(4, (it.items || []).length)),
    }));

    const Header = (
      <div className="bi-header">
        <div className="bi-header__titles">
          <h1 className="bi-title"><span className="bi-title__icon"><I name="shield-check" size={20} /></span>Site Audit</h1>
          <div className="bi-subtitle">
            Technical SEO{site ? " · " + site.name : ""} <span className="seo-free-tag" style={{ marginLeft: 6 }}>Free</span>
            {/* A snapshot from last week and a crawl that just finished are
                different things to act on, so the screen says which it is
                showing rather than letting stored figures pass for fresh. */}
            {storedAt && phase === "results" && (
              <span className="seo-audit__stamp"> · last run {dayOf(storedAt)}</span>
            )}
          </div>
        </div>
        <div className="bi-header-actions">
          {/* No chooser on a crawl-only site: there is only one pass it can
              run, and offering the other would offer a failure. */}
          {!crawlOnly && (
            <div className="kw-seg kw-seg--lg">
              {[["rest", "WordPress"], ["public", "Live site"]].map(([id, l]) =>
                <button key={id} className={mode === id ? "on" : ""} disabled={phase === "running"} onClick={() => setMode(id)}>{l}</button>)}
            </div>
          )}
          <button className="bi-btn bi-btn--primary" disabled={phase === "running"} onClick={run}>
            {phase === "running" ? <><span className="ml-spinner ml-spinner--sm ml-spinner--on-primary" style={{ width: 14, height: 14 }} />Auditing…</> : phase === "results" ? <><I name="refresh" size={16} />Re-run audit</> : <><I name="play" size={16} />Run audit</>}
          </button>
        </div>
      </div>
    );

    if (phase !== "results") return (
      <div className="bi-page">{Header}
        <div className="bi-empty-state">
          <span className="bi-empty-state__icon">{phase === "running" ? <span className="ml-spinner ml-spinner--lg" /> : <I name={phase === "error" ? "alert-circle" : "shield-check"} size={30} />}</span>
          <div className="bi-empty-state__title">{phase === "running" ? "Auditing your site…" : phase === "error" ? "Couldn't run the audit" : "Run a site audit"}</div>
          <p className="bi-empty-state__desc">{phase === "running"
            ? (useMode === "public" ? "Reading robots.txt and the sitemap, then crawling each public URL's <head>." : "Crawling published and draft posts and pages, checking on-page SEO.")
            : phase === "error" ? "Connect a site, then try again."
            : (useMode === "public"
                ? "Fetches your served HTML (robots.txt → sitemap → each page's <head>) and checks canonical, Open Graph and JSON-LD structured data. Best for headless sites. No AI cost."
                  + (crawlOnly ? " This site has no CMS, so the served HTML is all there is to read, and it is read without logging in anywhere." : "")
                : "Crawls published and draft posts and pages and flags on-page SEO issues. No AI cost.")}</p>
          {/* The duplicate probe compares the live site against a WordPress
              front end. A crawl-only site has none, so the field is not shown
              rather than inviting a URL that cannot exist. */}
          {phase !== "running" && useMode === "public" && !crawlOnly && (
            <div className="seo-crawl-opt">
              <input type="text" className="hds-input" placeholder="WordPress front-end URL (optional: duplicate check)" value={wpFrontEnd} onChange={e => setWpFrontEnd(e.target.value)} />
              <span className="seo-crawl-opt__hint">Set this to detect posts also reachable on WordPress's own URLs.</span>
            </div>
          )}
          {phase !== "running" && <div className="bi-empty-state__actions"><button className="bi-btn bi-btn--primary" onClick={run}><I name="play" size={16} />Run audit</button></div>}
        </div>
      </div>
    );

    return (
      <div className="bi-page">{Header}
        <div className="seo-stat-row">
          <SeoStat icon="x-circle" label="Errors" value={String(sum.error || 0)} />
          <SeoStat icon="alert-triangle" label="Warnings" value={String(sum.warning || 0)} />
          <SeoStat icon="info" label="Notices" value={String(sum.notice || 0)} />
          <SeoStat icon="file-text" label={res && res.source === "public" ? "URLs crawled" : "Pages crawled"} value={String((res && res.crawled) || 0)} />
        </div>

        {res && res.source === "public" && res.meta && (
          <div className="seo-crawl-meta">
            <span className={"seo-crawl-meta__chip" + (res.meta.robotsTxt ? " ok" : " warn")}><I name={res.meta.robotsTxt ? "check" : "alert-triangle"} size={13} />robots.txt {res.meta.robotsTxt ? "found" : "missing"}</span>
            <span className={"seo-crawl-meta__chip" + (res.meta.sitemapsRead > 0 ? " ok" : " warn")}><I name={res.meta.sitemapsRead > 0 ? "check" : "alert-triangle"} size={13} />{res.meta.sitemapsRead > 0 ? "Sitemap read" : "No sitemap"}</span>
            <span className="seo-crawl-meta__chip"><I name="clock" size={13} />{res.meta.lastmodCoverage}% have lastmod</span>
            {res.meta.wpDuplicateProbe && <span className="seo-crawl-meta__chip"><I name="copy" size={13} />WP duplicate probe on</span>}
          </div>
        )}

        {ISSUES.length === 0 && <div className="bi-subempty">No issues found - nice.</div>}
        <div className="bi-list">
          {ISSUES.map(it => {
            const open = expanded[it.id];
            const sevLabel = it.sev === "error" ? "Error" : it.sev === "warning" ? "Warning" : "Notice";
            return (
              <div className="bi-card seo-issue" key={it.id} onClick={() => setExpanded(e => ({ ...e, [it.id]: !e[it.id] }))}>
                <div className="seo-issue__head">
                  <span className="seo-issue__sev"><span className={"priority-badge priority-" + it.sev}>{sevLabel}</span></span>
                  <span className="seo-issue__label">{it.label}</span>
                  <span className="seo-issue__count">{it.count}</span>
                  <I name="chevron-right" size={16} className={"seo-issue__chev" + (open ? " open" : "")} />
                </div>
                {open && (
                  <div className="seo-issue__items" onClick={e => e.stopPropagation()}>
                    {it.items.map((x, i) => <div className="seo-issue__item" key={i}><I name="file-text" size={14} style={{ color: "var(--text-muted)" }} />{x}<a href="#" onClick={e => e.preventDefault()}>view <I name="external-link" size={11} /></a></div>)}
                    {it.more > 0 && <div className="seo-issue__more">…and {it.more} more</div>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  /* ── The whole website: the public lens over every section ─────────────────
     The figures are summed from the snapshots the sections already hold, so a
     section with none is a HOLE and not a zero, and the two stamps are both
     reported because three sections audited on three days is three ages. */
  function PropertyAudit({ site, sections }) {
    const anchorId = site ? site.id : null;
    const [phase, setPhase] = useState("loading"); // loading | results | error
    const [res, setRes] = useState(null);
    const [err, setErr] = useState(null);
    const [expanded, setExpanded] = useState({});
    const [running, setRunning] = useState(null);  // the section_id being crawled
    const [runErr, setRunErr] = useState(null);    // { id, msg }

    const load = useCallback(async (keepOnScreen) => {
      if (anchorId == null) { setErr("No site is connected yet, so there is no website to audit."); setPhase("error"); return; }
      if (!keepOnScreen) setPhase("loading");
      try {
        // The lens is public and is not asked for. At All the REST lens has no
        // subject: it reads one CMS, and a website is not one CMS.
        const r = await api(`/wp/audit-property?site_id=${anchorId}&source=public`);
        setRes(r); setErr(null); setPhase("results");
      } catch (e) {
        setErr((e && e.message) || "the stored audits of this website could not be read.");
        setPhase("error");
      }
    }, [anchorId]);
    useEffect(() => { load(); }, [load]);

    // Running an audit is a PER SECTION action, through the endpoint that
    // section's own screen uses. Nothing property-wide is run or stored here,
    // and 4.1 consequence 1 is the reason: one row per site and source means a
    // property-wide crawl would overwrite the section's own snapshot.
    const runSection = async (row) => {
      const url = fullUrlOf((sections || []).find(s => s.id === row.section_id));
      if (!url) {
        setRunErr({ id: row.section_id, msg: `${row.name} has no address stored, so there is nothing to crawl.` });
        return;
      }
      setRunning(row.section_id); setRunErr(null);
      try {
        await api(`/wp/audit-public?siteUrl=${encodeURIComponent(url)}&maxUrls=100`);
        await load(true);
      } catch (e) {
        setRunErr({ id: row.section_id, msg: (e && e.message) || "the crawl did not finish." });
      } finally { setRunning(null); }
    };

    const sum = (res && res.summary) || { error: 0, warning: 0, notice: 0 };
    const rows = (res && res.sections) || [];
    const never = (res && res.sections_never_audited) || [];
    const unreadable = (res && res.sections_unreadable) || [];
    const oldest = dayOf(res && res.oldest_audit);
    const newest = dayOf(res && res.newest_audit);

    const Header = (
      <div className="bi-header">
        <div className="bi-header__titles">
          <h1 className="bi-title"><span className="bi-title__icon"><I name="shield-check" size={20} /></span>Site Audit</h1>
          <div className="bi-subtitle">
            Technical SEO · the whole website <span className="seo-free-tag" style={{ marginLeft: 6 }}>Free</span>
            {/* Both stamps, never one. A single "last audited" over three
                sections crawled on three days would describe none of them. */}
            {oldest && <span className="seo-audit__stamp"> · stored audits from {oldest === newest ? oldest : oldest + " to " + newest}</span>}
          </div>
        </div>
        <div className="bi-header-actions">
          <button className="bi-btn" disabled={phase === "loading" || running != null} onClick={() => load(true)}>
            <I name="refresh" size={16} />Reload findings
          </button>
        </div>
      </div>
    );

    if (phase !== "results") return (
      <div className="bi-page">{Header}
        <div className="bi-empty-state">
          <span className="bi-empty-state__icon">{phase === "loading" ? <span className="ml-spinner ml-spinner--lg" /> : <I name="alert-circle" size={30} />}</span>
          <div className="bi-empty-state__title">{phase === "loading" ? "Reading this website's stored audits…" : "Couldn't read this website's audits"}</div>
          <p className="bi-empty-state__desc">{phase === "loading"
            ? "The findings at this position are the stored audits of every section, summed. Nothing is crawled to build it."
            : err}</p>
          {phase === "error" && <div className="bi-empty-state__actions"><button className="bi-btn bi-btn--primary" onClick={() => load()}><I name="refresh" size={16} />Try again</button></div>}
        </div>
      </div>
    );

    return (
      <div className="bi-page">{Header}
        <div className="seo-stat-row">
          <SeoStat icon="x-circle" label="Errors" value={String(sum.error || 0)} />
          <SeoStat icon="alert-triangle" label="Warnings" value={String(sum.warning || 0)} />
          <SeoStat icon="info" label="Notices" value={String(sum.notice || 0)} />
          <SeoStat icon="file-text" label="URLs these findings cover" value={String((res && res.crawled) || 0)} />
        </div>

        {/* How much of the website these findings cover, in the backend's own
            words. The figures above are arithmetic over the sections that have
            a stored audit, and that is not always every section.
            BOTH STAMPS, named: three sections audited on three days is three
            ages, and one "last audited" over them would describe none of them.
            Each section's own stamp is on its line below. */}
        <p className="seo-intro">
          {res.coverage_why}
          {oldest && <> Oldest stored audit: {oldest}. Newest: {newest}.</>}
        </p>

        {/* A HOLE IS NOT A ZERO. "This section has no findings" and "nobody has
            looked at this section" are the same empty list on screen and not
            the same fact, so the sections are named rather than counted clean. */}
        {never.length > 0 && (
          <div className="bi-hint" style={{ marginBottom: 12 }}>
            <b>Nobody has audited {never.map(x => x.name).join(", ")} yet.</b>{" "}
            Those sections contribute nothing to the figures above, which is a gap in the measurement and not a clean
            result. Each one has its own Run below.
          </div>
        )}
        {unreadable.length > 0 && (
          <div className="ds-strip ds-strip--error" style={{ marginBottom: 12 }}>
            <I name="alert-triangle" size={14} />
            <span>
              The stored audit of {unreadable.map(x => `${x.name} (${x.error})`).join(", ")} could not be read, which is
              not the same as that section never having been audited: running one again may not be what is needed.
            </span>
          </div>
        )}

        {/* ONE LINE PER SECTION, each with its own Run and its own export,
            which is the per row verb MODULE-SCOPE 1.2 asks for and 4.1
            consequence 2 argues: three jobs usually have three owners. */}
        <div className="bi-results-head"><I name="layers" size={14} />The sections of this website</div>
        <div className="bi-hint" style={{ marginBottom: 12 }}>
          <b>An audit is run and exported per section, and there is no single document for the website.</b>{" "}
          One file telling a developer to fix titles across two CMSs and a static build is three jobs in one document,
          and three jobs usually have three owners. The WordPress lens, which also reads drafts, is a section's own
          question: put the filter on that section to reach it.
        </div>
        <div className="seo-prop-list" style={{ marginBottom: 22 }}>
          {rows.map(row => {
            const busy = running === row.section_id;
            const sectionUrl = fullUrlOf((sections || []).find(s => s.id === row.section_id));
            return (
              <div className="seo-prop" key={row.section_id} style={{ alignItems: "flex-start" }}>
                <span className="seo-prop__icon"><I name={row.source === "crawl" ? "file-text" : "layers"} size={16} /></span>
                <div className="seo-prop__meta">
                  <div className="seo-prop__url" style={{ fontFamily: "var(--font-sans)" }}>{row.name}</div>
                  <div className="seo-prop__type">
                    {row.lens === "rest" ? "WordPress lens" : "Live site lens"}
                    {row.audited === true && <> · {row.errors} errors, {row.warnings} warnings, {row.notices} notices over {row.crawled} URLs · crawled {dayOf(row.updated_at) || "at an unknown time"}</>}
                    {row.audited === false && <> · never audited</>}
                    {row.audited === null && <> · the stored audit could not be read</>}
                  </div>
                  {/* A crawl-only section has its lens FORCED, which is a fact
                      about the section and not a choice somebody made. */}
                  {row.lens_forced && row.lens_why && <p className="bm-kw" style={{ marginTop: 6 }}>{row.lens_why}</p>}
                  {row.audited !== true && row.why && <p className="bm-kw" style={{ marginTop: 6 }}>{row.why}</p>}
                  {row.verb === "handover_brief" && row.verb_why && <p className="bm-kw" style={{ marginTop: 6 }}>{row.verb_why}</p>}
                  {row.title_suffix && (
                    <p className="bm-kw" style={{ marginTop: 6 }}>
                      This front end appends <b>{row.title_suffix}</b> to every title it serves, so the title budget on
                      this section is shorter than 60 by that much. A suffix belongs to a front end, so a website with
                      two front ends has two budgets.
                    </p>
                  )}
                  {row.crawled_root_note && <p className="bm-kw" style={{ marginTop: 6 }}>{row.crawled_root_note}</p>}
                  {/* The run and the export are pointed at the section's own
                      address, which is this workspace's connection to it. A
                      piece of the website this workspace holds no connection to
                      still has findings and still has no button. */}
                  {!sectionUrl && (
                    <p className="bm-kw" style={{ marginTop: 6 }}>
                      No address for {row.name} is held in this workspace, so it cannot be run or exported from here.
                    </p>
                  )}
                  {runErr && runErr.id === row.section_id && (
                    <p className="bm-kw bm-kw--over" style={{ marginTop: 6 }}>{runErr.msg}</p>
                  )}
                </div>
                <div style={{ display: "flex", gap: 8, flex: "0 0 auto", alignItems: "center" }}>
                  <button className="bi-btn bi-btn--sm" disabled={busy || !sectionUrl} onClick={() => runSection(row)}>
                    {busy
                      ? <><span className="ml-spinner ml-spinner--sm" style={{ width: 13, height: 13 }} />Auditing…</>
                      : <><I name={row.audited === true ? "refresh" : "play"} size={14} />{row.audited === true ? "Re-run" : "Run"}</>}
                  </button>
                  {/* The export is per section for the same reason the run is.
                      Offered only where a snapshot exists: the route refuses
                      with "no audit stored" otherwise, and a button that can
                      only fail is worse than no button. */}
                  {row.audited === true && sectionUrl && (
                    <a className="bm-post__view" href={`/api/wp/audit-handoff?siteUrl=${encodeURIComponent(sectionUrl)}&format=pdf&source=public&download=1`}>
                      <I name="download" size={12} />Export
                    </a>
                  )}
                </div>
              </div>
            );
          })}
          {rows.length === 0 && <div className="bi-subempty">This website has no sections to audit yet.</div>}
        </div>

        <div className="bi-results-head"><I name="shield-check" size={14} />Findings, grouped by issue</div>
        {(res.issues || []).length === 0 && (
          <div className="bi-subempty">
            {rows.some(r => r.audited === true)
              ? "No findings in the stored audits of this website."
              : "No section of this website has a stored audit yet, so there is nothing to group."}
          </div>
        )}
        <div className="bi-list">
          {(res.issues || []).map(it => {
            const open = expanded[it.key];
            const sevLabel = it.severity === "error" ? "Error" : it.severity === "warning" ? "Warning" : "Notice";
            return (
              <div className="bi-card seo-issue" key={it.key} onClick={() => setExpanded(e => ({ ...e, [it.key]: !e[it.key] }))}>
                <div className="seo-issue__head">
                  <span className="seo-issue__sev"><span className={"priority-badge priority-" + it.severity}>{sevLabel}</span></span>
                  <span className="seo-issue__label">{it.label}</span>
                  {/* The count is the STORED count, which is the whole figure.
                      The list below it is a sample and never the count. */}
                  <span className="seo-issue__count">{it.count}</span>
                  <I name="chevron-right" size={16} className={"seo-issue__chev" + (open ? " open" : "")} />
                </div>
                {open && (
                  <div className="seo-issue__items" onClick={e => e.stopPropagation()}>
                    {/* Which sections this issue is spread across, before any
                        URL: the count above is arithmetic over them. */}
                    <div className="seo-crawl-meta" style={{ marginBottom: 2 }}>
                      {(it.sections || []).map(s => (
                        <span className="seo-crawl-meta__chip" key={s.section_id}>{s.name} {s.count}</span>
                      ))}
                    </div>
                    {/* THE SAMPLED WARNING. A stored audit keeps at most 50
                        examples per issue per section, so the list sets the
                        order and the count above is the figure to act on. */}
                    {it.sampled && it.sampled_why && (
                      <p className="bm-kw bm-kw--over" style={{ marginTop: 0 }}>{it.sampled_why}</p>
                    )}
                    {(it.items || []).map((x, i) => (
                      <div className="seo-issue__item" key={i}>
                        <I name="file-text" size={14} style={{ color: "var(--text-muted)" }} />
                        {/* Every affected URL names its section: that is 3.1's
                            explicit requirement for this screen, because a URL
                            with no section is a finding nobody can be given. */}
                        <span className="seo-crawl-meta__chip" style={{ flex: "0 0 auto" }}>{x.section_name}</span>
                        {x.can_publish === false && <span className="seo-crawl-meta__chip warn" style={{ flex: "0 0 auto" }}>no CMS</span>}
                        <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {(x.title || x.link || "") + (x.title && x.link ? " · " + x.link : "")}
                        </span>
                        {x.link && <a href={x.link} target="_blank" rel="noopener noreferrer">view <I name="external-link" size={11} /></a>}
                      </div>
                    ))}
                    <div className="seo-issue__more">
                      {it.listed} of the {it.count} affected URLs are listed here.
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  /* The switcher position decides WHICH screen this is, and the two are
     separate components on purpose: an early return inside one of them would
     change its hook count the moment the filter moved, which React refuses
     outright. Switching component type remounts instead, so each position
     starts with its own state rather than inheriting the other's phase. */
  function SiteAudit({ state, site, sectionId, sections }) {
    return sectionId == null
      ? <PropertyAudit site={site} sections={sections || []} />
      : <SectionAudit site={site} />;
  }

  window.MlSeoAudit = { SiteAudit };
})();
